import * as React from 'react';
import type { FileRejection } from 'react-dropzone';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import type DocumentRowVM from 'ab-viewModels/document/table.viewModel';

import { prettyBytes } from '@acceligentllc/shared/utils/formatting';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import { errorToast, loadingToast, successToast } from '@acceligentllc/storybook';

import Breadcrumbs from 'af-components/Breadcrumbs';
import type { OwnProps as TableOwnProps, TabProps, Column, ButtonData, LegacyTableRef } from 'af-components/Table6';
import _Table from 'af-components/Table6';
import LastUpdatedByCell from 'af-components/Table6/Cells/LastUpdatedByCell';
import type TableComponent from 'af-components/Table6/Table';

import * as AttachmentActions from 'af-actions/attachment';
import * as DocumentActions from 'af-actions/document';

import UploadDocumentModal from './UploadDocumentModal';

const Table = _Table as unknown as React.ComponentClass<TableOwnProps<DocumentRowVM>>;

const DELETE_CONFIRMATION_BODY = (
	<>
		You are about to delete selected documents.
		<br />
		Are you sure you want to continue.
	</>
);

type OwnProps = CustomRouteComponentProps;

type Props = OwnProps & ConnectedProps<typeof connector>;

const deleteDocumentModalTitle = () => 'Delete Document';
const deleteDocumentModalBody = (original: DocumentRowVM) => (
	<>
		You are about to delete document {original.name}.
		<br />
		Are you sure you want to continue.
	</>
);
const deleteDocumentModalText = () => 'Remove';

const BREADCRUMBS = [{ label: 'Document' }];

const Documents: React.FC<Props> = (props) => {
	const { getDocuments, uploadDocuments, deleteAllDocuments, deleteDocument, downloadAttachment } = props;

	const tableRef = React.useRef<TableComponent<DocumentRowVM>>(null);
	const timerRef = React.useRef<Nullable<NodeJS.Timeout>>(null);

	const [filesError, setFilesError] = React.useState('');
	const [uploadingDocuments, setUploadingDocuments] = React.useState<File[]>([]);
	const [showModal, setShowModal] = React.useState(false);

	React.useEffect(() => {
		return () => {
			if (!timerRef.current) {
				return;
			}
			clearInterval(timerRef.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const openModal = React.useCallback(async () => setShowModal(true), []);
	const closeModal = React.useCallback(() => setShowModal(false), []);

	const columns: Column<DocumentRowVM>[] = [
		{
			Header: 'File Name',
			accessor: 'name',
			Cell: ({ original }) => (
				<a
					className="text-blue"
					href="#"
					onClick={downloadDocument.bind(this, original)}
				>
					{original.name}
				</a>
			),
		},
		{
			Header: 'Size',
			accessor: 'size',
			Cell: ({ original }) => <span>{prettyBytes(original.size)}</span>,
		},
		{
			Header: 'Uploaded',
			accessor: 'uploadedOn',
			headerClassName: 'align-right padding-right-l',
			width: 200,
			Cell: ({ original }) => <LastUpdatedByCell updatedAt={original.uploadedOn} updatedBy={original.uploadedByObject} />,
		},
	];

	const tabs = (): TabProps<DocumentRowVM>[] => {
		const buttons: ButtonData[] = [
			{
				type: TableButtonType.LINK,
				hasPermission: true,
				label: 'Upload',
				icon: 'ActionImport',
				onClick: openModal,
			},
		];

		return [
			{
				label: 'Documents',
				columns,
				selectable: true,
				buttons,
				fetch: getDocuments,
				bulkDelete: onDeleteAllDocuments,
				bulkDeleteConfirmationBody: DELETE_CONFIRMATION_BODY,
				rowActions: [
					{
						label: 'Download',
						action: downloadDocument,
						shouldRefresh: false,
					},
					{
						label: 'Delete',
						action: onDeleteDocument,
						hasModal: true,
						modalTitle: deleteDocumentModalTitle,
						modalBody: deleteDocumentModalBody,
						modalText: deleteDocumentModalText,
						shouldRefresh: true,
					},
				],
			},

		];
	};

	const uploadFiles = React.useCallback(async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
		if (fileRejections.length > 0) {
			setFilesError('Following files are rejected: ' + fileRejections.map((r) => r.file.name + ' '));
			timerRef.current = setInterval(() => setFilesError(''), 7000);
			closeModal();
			return;
		}

		const toastId = 'resources-documents-upload';
		try {
			setUploadingDocuments(uploadingDocuments.concat(acceptedFiles));

			loadingToast(
				toastId,
				{ title: 'Uploading document...', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
			);

			await uploadDocuments({ requestFiles: acceptedFiles });

			setUploadingDocuments(uploadingDocuments.filter((d) => !acceptedFiles.includes(d)));
			tableRef.current?.refreshTable();
			successToast(
				toastId,
				{ title: 'Upload complete', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
			);

		} catch ({ errors }) {
			if (errors?.files) {
				setFilesError(errors.files);
				timerRef.current = setInterval(() => setFilesError(''), 7000);
			}
			errorToast(
				toastId,
				{ title: 'Upload failed', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
			);
		}
		closeModal();
	}, [closeModal, uploadDocuments, uploadingDocuments]);

	const onDeleteDocument = React.useCallback(async ({ id }: DocumentRowVM) => {
		await deleteDocument(id);
	}, [deleteDocument]);

	const downloadDocument = React.useCallback(async (original: DocumentRowVM) => {
		const toastId = `resources-documents-download-${original.id}`;

		loadingToast(
			toastId,
			{ title: 'Downloading document...', description: original.name, timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
		);

		await downloadAttachment(original.id, original.name);

		successToast(
			toastId,
			{ title: 'Download complete', description: original.name, timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
		);
	}, [downloadAttachment]);

	const onDeleteAllDocuments = React.useCallback(async (ids: number[]) => {
		await deleteAllDocuments(ids);
		if (tableRef?.current) {
			tableRef.current.refreshTable();
		}
	}, [tableRef, deleteAllDocuments]);

	return (
		<div className="form-segment form-segment--maxi" >
			<Breadcrumbs items={BREADCRUMBS} />
			<Table
				ref={tableRef as unknown as LegacyTableRef<DocumentRowVM>}
				tableName={TableNameEnum.DOCUMENTS}
				tabs={tabs()}
			/>
			<UploadDocumentModal
				error={filesError}
				onCancel={closeModal}
				onDrop={uploadFiles}
				showModal={showModal}
			/>
		</div>
	);
};

function mapDispatchToProps() {
	return {
		getDocuments: DocumentActions.getDocuments,
		uploadDocuments: DocumentActions.uploadDocuments,
		deleteDocument: DocumentActions.deleteDocument,
		deleteAllDocuments: DocumentActions.deleteAllDocuments,
		downloadAttachment: AttachmentActions.downloadAttachment,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(Documents);
